import { Component, Input, ChangeDetectionStrategy, inject } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'hc-form-field',
  templateUrl: './form-field.component.html',
  styleUrls: ['./form-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class HcFormFieldComponent {
  @Input() label = '';
  @Input() optional = false;
  @Input() required = false;
  /** Show the publish-required warning icon (⚠) next to the label when true */
  @Input() publishRequired = false;
  @Input() helper = '';
  @Input() fieldId = '';

  /**
   * Control type hint for alignment.
   * When 'checkbox' or 'radio', adds top padding so the control aligns
   * with the input body of neighboring form fields in a flex row.
   */
  @Input() controlType: 'input' | 'checkbox' | 'radio' = 'input';

  // Character count display (e.g., "45/200")
  @Input() charCount = 0;
  @Input() charMax = 0;

  /**
   * Explicit error message — takes priority over auto-detection.
   * When set to a non-empty string, displays this error.
   */
  @Input() error = '';

  /**
   * Form control for auto-validation.
   * When provided, auto-detects errors from the control (touched + invalid).
   * Works with both reactive forms (AbstractControl) and template-driven (#ref.control).
   */
  @Input() control: AbstractControl | null = null;

  /**
   * Custom error message — used as the error text when control has errors.
   * When not set, auto-generates error messages from the control's validation errors.
   */
  @Input() customError = '';

  /**
   * When to show the auto-detected error. Default: 'touched'.
   */
  @Input() showErrorWhen: 'touched' | 'dirty' | 'always' = 'touched';

  /**
   * Suppress auto-error display. Use when handling errors externally.
   */
  @Input() hideErrors = false;

  private translate = inject(TranslateService);

  /** Whether the field is in error state (for CSS class + child hc-input) */
  get hasError(): boolean {
    if (this.error) return true;
    if (this.hideErrors || !this.control) return false;

    switch (this.showErrorWhen) {
      case 'touched': return !!(this.control.invalid && this.control.touched);
      case 'dirty': return !!(this.control.invalid && this.control.dirty);
      case 'always': return !!this.control.invalid;
      default: return false;
    }
  }

  /** The error message to display */
  get displayError(): string {
    // Explicit error always wins
    if (this.error) return this.error;
    if (this.hideErrors || !this.control || !this.hasError) return '';

    // Custom error message
    if (this.customError) return this.customError;

    // Auto-generate from validation errors
    return this.getAutoErrorMessage();
  }

  private getAutoErrorMessage(): string {
    if (!this.control?.errors) return '';
    const errors = this.control.errors;

    if (errors['required']) {
      return this.label
        ? this.translate.instant('VALIDATION.REQUIRED', { field: this.label })
        : this.translate.instant('VALIDATION.REQUIRED_DEFAULT');
    }
    if (errors['email']) return this.translate.instant('VALIDATION.EMAIL');
    if (errors['minlength']) return this.translate.instant('VALIDATION.MINLENGTH', { length: errors['minlength'].requiredLength });
    if (errors['maxlength']) return this.translate.instant('VALIDATION.MAXLENGTH', { length: errors['maxlength'].requiredLength });
    if (errors['min']) return this.translate.instant('VALIDATION.MIN', { min: errors['min'].min });
    if (errors['max']) return this.translate.instant('VALIDATION.MAX', { max: errors['max'].max });
    if (errors['pattern']) return this.translate.instant('VALIDATION.PATTERN');
    if (errors['serverError']) return errors['serverError'];

    return this.translate.instant('VALIDATION.INVALID');
  }
}
